/**
 * The shipped sidebar, as a fill (RFC 0284 S3).
 *
 * This file is the point of S3. `SidebarPanel` is unchanged; what changes is
 * that `WorldShell` no longer imports it. The shell asks the registry what fills
 * the sidebar slot, and the answer happens to be built in.
 *
 * WHAT IT PROVES, and why the RFC calls it the whole idea: a registry whose own
 * built-ins bypass it is a contract nobody has tested. Registering the shipped
 * sidebar through the same door a third party will use means any capability the
 * built-in needs and the contract lacks is a compile error here, today, rather
 * than a bug report from the first person who writes one.
 *
 * WHAT IT ALREADY SHOWS. `SidebarSlotProps` is 21 fields and eleven of them are
 * commands on the window: open, open beside, open a session, close an open item,
 * collapse, pin, and the four space verbs. That is `mod.window` (§3c) written out
 * by hand, and it is the measure of how much S2 has to cover before an INSTALLED
 * sidebar is possible. A built-in can take them as props because it is in our
 * realm; a sandboxed one cannot take a function over postMessage at all.
 */

import { SidebarPanel } from '../../window/SidebarPanel'
import type { BuiltinFill } from '../slots'

/**
 * What the sidebar slot is handed.
 *
 * Derived from the component rather than restated, so the props and the slot
 * contract cannot drift apart. When S2 replaces the eleven command props with
 * one `window` object, this type changes in one place and every caller follows.
 */
export type SidebarSlotProps = React.ComponentProps<typeof SidebarPanel>

export const BUILTIN_SIDEBAR_ID = 'mod.sidebar'

export const builtinSidebar: BuiltinFill<'sidebar'> = {
  id: BUILTIN_SIDEBAR_ID,
  title: 'Sidebar',
  slot: 'sidebar',
  builtin: true,
  render: (props) => <SidebarPanel {...props} />,
}
